import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type BrokerRecord = {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  type?: string | null;
};

export type BrokerDraft = {
  name: string;
  phone: string;
  email?: string;
  fatherName?: string;
  cnic?: string;
};

type BrokerApiRecord = {
  id?: number | string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  type?: string | null;
};

const LS_KEY = "inventory.brokers.v1";

function mapBroker(raw: BrokerApiRecord): BrokerRecord {
  return {
    id: String(raw.id ?? ""),
    name: String(raw.name ?? "").trim() || `Broker #${raw.id ?? ""}`,
    phone: String(raw.phone ?? "").trim(),
    email: raw.email ? String(raw.email) : null,
    type: raw.type ? String(raw.type) : "broker",
  };
}

function readCache(): BrokerRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as BrokerApiRecord[];
    return Array.isArray(parsed) ? parsed.map(mapBroker).filter((b) => b.id) : [];
  } catch {
    return [];
  }
}

function writeCache(rows: BrokerRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(rows));
}

export function listBrokers(): BrokerRecord[] {
  return readCache();
}

export async function syncBrokersFromApi(): Promise<BrokerRecord[]> {
  const response = await api.get<{ data?: BrokerApiRecord[] }>("/api/v1/brokers", {
    params: { per_page: 200 },
  });
  const rows = (response.data?.data ?? []).map(mapBroker).filter((b) => b.id);
  writeCache(rows);
  return rows;
}

export function emptyBrokerDraft(): BrokerDraft {
  return { name: "", phone: "", email: "", fatherName: "", cnic: "" };
}

export function validateBrokerDraft(draft: BrokerDraft): { valid: boolean; message?: string } {
  if (!draft.name.trim()) return { valid: false, message: "Broker name is required." };
  if (!draft.phone.trim()) return { valid: false, message: "Contact number is required." };
  return { valid: true };
}

export function isBrokerDraftComplete(draft: BrokerDraft): boolean {
  return validateBrokerDraft(draft).valid;
}

export async function createBroker(draft: BrokerDraft): Promise<{ record: BrokerRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: BrokerApiRecord }>("/api/v1/brokers", {
    name: draft.name.trim(),
    phone: draft.phone.trim(),
    email: draft.email?.trim() || null,
    father_name: draft.fatherName?.trim() || null,
    cnic: draft.cnic?.trim() || null,
    type: "broker",
    entity_kind: "individual",
  });
  const record = mapBroker(response.data?.data ?? {});
  const existing = readCache().filter((b) => b.id !== record.id);
  writeCache([record, ...existing]);
  return {
    record,
    message: response.data?.message ?? "Broker created successfully.",
  };
}

export function brokerErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function formatBrokerDisplayName(broker: BrokerRecord): string {
  const phone = broker.phone?.trim();
  return phone ? `${broker.name} (${phone})` : broker.name;
}
